import { DateTime } from 'luxon';
import type { StoreApi } from 'zustand';

/** The zone used when the signed-in agency has none, or an invalid one. */
export const PACIFIC_TIMEZONE = 'America/Los_Angeles';

let agencyTimezone: string = PACIFIC_TIMEZONE;

const isValidTimezone = (timeZone: string): boolean =>
  DateTime.now().setZone(timeZone).isValid;

/**
 * Record the signed-in agency's timezone for the date helpers.
 *
 * The store calls this whenever its user changes, so helpers that run outside
 * React (form schemas, plain utils) can read the zone without a hook. The
 * store pushes the value here rather than the helpers reading the store,
 * because the store's own imports already reach the date helpers.
 *
 * A missing or unrecognised zone falls back to Pacific, the same fallback the
 * backend applies.
 */
export const setAgencyTimezone = (
  timeZone: string | null | undefined
): void => {
  agencyTimezone =
    timeZone && isValidTimezone(timeZone) ? timeZone : PACIFIC_TIMEZONE;
};

/** The signed-in agency's IANA timezone, or Pacific when it has none. */
export const getAgencyTimezone = (): string => agencyTimezone;

interface AgencyTimezoneState {
  user: { agency?: { timezone?: string | null } | null } | null;
}

/**
 * Keep the agency timezone in step with a store's signed-in user, starting
 * with the user already in it (one restored from storage, for example).
 * Returns the unsubscribe function.
 */
export const syncAgencyTimezone = <S extends AgencyTimezoneState>(
  store: Pick<StoreApi<S>, 'getState' | 'subscribe'>
): (() => void) => {
  setAgencyTimezone(store.getState().user?.agency?.timezone);
  return store.subscribe((state, previous) => {
    if (state.user !== previous.user) {
      setAgencyTimezone(state.user?.agency?.timezone);
    }
  });
};
